import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, AlertCircle } from 'lucide-react';
import { API_BASE } from '../services/apiBase';

const API    = API_BASE;
const TENANT = '569593dd-38e2-4d69-bdca-5d07be8b46f1';

function authHeaders() {
  return { Authorization: 'Bearer ' + localStorage.getItem('access_token') };
}

const DAYS      = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const DAY_VALS  = [1,2,3,4,5,6,7];

const ACTION_TYPES = [
  { value: 'play_category',   label: 'Play from Category',  desc: 'Pick the next track from a rotation category' },
  { value: 'switch_clock',    label: 'Switch Clock',         desc: 'Switch to a different clock template' },
  { value: 'play_track',      label: 'Play Specific Track',  desc: 'Queue a specific music track immediately' },
  { value: 'announcement',    label: 'Announcement',         desc: 'Trigger a text or audio announcement' },
];

const PRIORITY_COLOR = (p) => {
  if (p >= 8) return { color: '#ef4444', bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.3)' };
  if (p >= 5) return { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)' };
  return { color: '#22c55e', bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.3)' };
};

const EMPTY_FORM = {
  name: '', trigger_time: '08:00',
  days_of_week: [1,2,3,4,5,6,7],
  date_from: '', date_to: '',
  action_type: 'play_category',
  action_data: {},
  priority: 5, interrupt: false, is_active: true,
};

// ── Toggle switch ─────────────────────────────────────────────────────────────
function Toggle({ value, onChange, accent }) {
  const bg = value ? (accent || '#22c55e') : '#30363d';
  return (
    <div onClick={() => onChange(!value)} style={{ width: 36, height: 20, borderRadius: 10, background: bg, position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
      <div style={{ position: 'absolute', top: 2, left: value ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.18s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
    </div>
  );
}

// ── Priority slider ───────────────────────────────────────────────────────────
function PrioritySlider({ value, onChange }) {
  const { color } = PRIORITY_COLOR(value);
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#8b949e', letterSpacing: '0.06em' }}>PRIORITY</span>
        <span style={{ fontSize: 14, fontWeight: 800, color, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
      </div>
      <div style={{ position: 'relative', height: 6, borderRadius: 3, background: '#21262d' }}>
        <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${(value - 1) / 9 * 100}%`, background: color, borderRadius: 3, transition: 'width 0.1s, background 0.2s' }} />
        <input type="range" min={1} max={10} value={value} onChange={e => onChange(Number(e.target.value))}
          style={{ position: 'absolute', top: '50%', left: 0, width: '100%', transform: 'translateY(-50%)', opacity: 0, cursor: 'pointer', height: 20, margin: 0 }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, fontSize: 10, color: '#484f58' }}>
        <span>1 — low</span><span>10 — critical</span>
      </div>
    </div>
  );
}

// ── Event form modal ──────────────────────────────────────────────────────────
function EventModal({ event, stores, categories, clocks, onSave, onClose }) {
  const [form, setForm]     = useState(event ? {
    name: event.name, trigger_time: event.trigger_time,
    days_of_week: event.days_of_week || [1,2,3,4,5,6,7],
    date_from: event.date_from || '', date_to: event.date_to || '',
    action_type: event.action_type, action_data: event.action_data || {},
    priority: event.priority ?? 5, interrupt: event.interrupt ?? false,
    is_active: event.is_active ?? true,
  } : { ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const fad = (k, v) => setForm(p => ({ ...p, action_data: { ...p.action_data, [k]: v } }));
  const toggleDay = (d) => f('days_of_week', form.days_of_week.includes(d) ? form.days_of_week.filter(x => x !== d) : [...form.days_of_week, d].sort((a,b)=>a-b));

  const save = async () => {
    if (!form.name || !form.trigger_time) return;
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.detail || e.message)); }
    setSaving(false);
  };

  const iStyle = { width: '100%', background: '#0d1117', border: '1px solid #30363d', borderRadius: 8, padding: '8px 12px', color: '#e6edf3', fontSize: 13, boxSizing: 'border-box', outline: 'none' };
  const lStyle = { display: 'block', fontSize: 11, fontWeight: 700, color: '#8b949e', letterSpacing: '0.06em', marginBottom: 5, textTransform: 'uppercase' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: 520, background: '#161b22', border: '1px solid #30363d', borderRadius: 14, padding: 28, maxHeight: '90vh', overflowY: 'auto' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#e6edf3' }}>{event ? 'Edit Event' : 'New Automation Event'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#8b949e', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Name */}
          <div>
            <label style={lStyle}>Event Name</label>
            <input value={form.name} onChange={e => f('name', e.target.value)} placeholder="e.g. Morning News Break, Hourly Jingle…" style={iStyle} />
          </div>

          {/* Trigger time */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lStyle}>Trigger Time</label>
              <input type="time" value={form.trigger_time} onChange={e => f('trigger_time', e.target.value)} style={iStyle} />
            </div>
            <div>
              <label style={lStyle}>Store</label>
              <select value={form.store_id || ''} onChange={e => f('store_id', e.target.value || null)} style={iStyle}>
                <option value="">All Stores</option>
                {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>

          {/* Days of week */}
          <div>
            <label style={lStyle}>Days of Week</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {DAY_VALS.map((d, i) => {
                const active = form.days_of_week.includes(d);
                return (
                  <button key={d} onClick={() => toggleDay(d)} style={{
                    flex: 1, padding: '7px 0', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700,
                    background: active ? '#2f81f7' : '#21262d', color: active ? '#fff' : '#484f58', transition: 'all 0.15s',
                  }}>{DAYS[i]}</button>
                );
              })}
            </div>
          </div>

          {/* Date range (optional) */}
          <div style={{ padding: '12px 14px', background: '#1c2333', borderRadius: 10, border: '1px solid #30363d' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b', marginBottom: 8 }}>⭐ SEASONAL DATE RANGE (optional)</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={lStyle}>Active From</label>
                <input type="date" value={form.date_from} onChange={e => f('date_from', e.target.value)} style={iStyle} />
              </div>
              <div>
                <label style={lStyle}>Active Until</label>
                <input type="date" value={form.date_to} onChange={e => f('date_to', e.target.value)} style={iStyle} />
              </div>
            </div>
          </div>

          {/* Action type */}
          <div>
            <label style={lStyle}>Action</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {ACTION_TYPES.map(a => (
                <button key={a.value} onClick={() => f('action_type', a.value)} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, border: `1px solid ${form.action_type === a.value ? '#2f81f7' : '#30363d'}`,
                  background: form.action_type === a.value ? 'rgba(47,129,247,0.1)' : '#0d1117',
                  cursor: 'pointer', textAlign: 'left',
                }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: form.action_type === a.value ? '#2f81f7' : '#30363d', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#e6edf3' }}>{a.label}</div>
                    <div style={{ fontSize: 11, color: '#8b949e' }}>{a.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Action-specific fields */}
          {form.action_type === 'play_category' && (
            <div>
              <label style={lStyle}>Rotation Category</label>
              <select value={form.action_data.category_id || ''} onChange={e => fad('category_id', e.target.value)} style={iStyle}>
                <option value="">— Select category —</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          {form.action_type === 'switch_clock' && (
            <div>
              <label style={lStyle}>Clock Template</label>
              <select value={form.action_data.clock_id || ''} onChange={e => fad('clock_id', e.target.value)} style={iStyle}>
                <option value="">— Select clock —</option>
                {clocks.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          {form.action_type === 'announcement' && (
            <div>
              <label style={lStyle}>Announcement Text</label>
              <textarea value={form.action_data.text || ''} onChange={e => fad('text', e.target.value)} rows={3} placeholder="Text to announce…"
                style={{ ...iStyle, resize: 'vertical', lineHeight: '1.5' }} />
            </div>
          )}

          {/* Priority + interrupt */}
          <PrioritySlider value={form.priority} onChange={v => f('priority', v)} />

          <div style={{ display: 'flex', gap: 20, alignItems: 'center', padding: '12px 14px', background: '#0d1117', borderRadius: 8, border: '1px solid #30363d' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#e6edf3', marginBottom: 2 }}>Interrupt current track</div>
              <div style={{ fontSize: 11, color: '#8b949e' }}>Stop the current song immediately when this event fires</div>
            </div>
            <Toggle value={form.interrupt} onChange={v => f('interrupt', v)} accent="#ef4444" />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 13, color: '#8b949e' }}>Event enabled</div>
            <Toggle value={form.is_active} onChange={v => f('is_active', v)} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '9px', borderRadius: 8, border: '1px solid #30363d', background: 'transparent', color: '#8b949e', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
          <button onClick={save} disabled={saving || !form.name}
            style={{ flex: 2, padding: '9px', borderRadius: 8, border: 'none', background: saving || !form.name ? '#21262d' : '#2f81f7', color: saving || !form.name ? '#484f58' : '#fff', cursor: saving || !form.name ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700 }}>
            {saving ? 'Saving…' : event ? 'Save Changes' : 'Create Event'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Event row ─────────────────────────────────────────────────────────────────
function EventRow({ event, categories, clocks, onEdit, onDelete, onToggle }) {
  const [hovered, setHovered] = useState(false);
  const pc = PRIORITY_COLOR(event.priority);
  const days = (event.days_of_week || []).map(d => DAYS[(Number(d) - 1) % 7]).join(', ');
  const actionMeta = ACTION_TYPES.find(a => a.value === event.action_type);
  const cat = categories.find(c => c.id === event.action_data?.category_id);
  const clk = clocks.find(c => c.id === event.action_data?.clock_id);
  const actionDetail = cat?.name || clk?.name || event.action_data?.text?.slice(0, 30) || '—';

  return (
    <tr
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ background: hovered ? '#21262d' : 'transparent', borderBottom: '1px solid #21262d', transition: 'background 0.1s', opacity: event.is_active ? 1 : 0.5 }}
    >
      {/* Active toggle */}
      <td style={{ padding: '12px 14px', width: 44, textAlign: 'center' }}>
        <Toggle value={event.is_active} onChange={v => onToggle(event.id, v)} />
      </td>

      {/* Name */}
      <td style={{ padding: '12px 14px' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#e6edf3' }}>{event.name}</div>
        {event.interrupt && (
          <span style={{ fontSize: 10, fontWeight: 700, color: '#ef4444', letterSpacing: '0.04em' }}>⚡ INTERRUPT</span>
        )}
      </td>

      {/* Trigger */}
      <td style={{ padding: '12px 14px', width: 130 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#58a6ff', fontVariantNumeric: 'tabular-nums' }}>{event.trigger_time}</div>
        <div style={{ fontSize: 11, color: '#8b949e', marginTop: 2 }}>{days || '—'}</div>
      </td>

      {/* Action */}
      <td style={{ padding: '12px 14px', width: 180 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#8b949e', marginBottom: 2 }}>{actionMeta?.label}</div>
        <div style={{ fontSize: 12, color: '#e6edf3', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{actionDetail}</div>
      </td>

      {/* Priority */}
      <td style={{ padding: '12px 14px', width: 80, textAlign: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 800, padding: '3px 10px', borderRadius: 6, background: pc.bg, border: `1px solid ${pc.border}`, color: pc.color }}>
          {event.priority}
        </span>
      </td>

      {/* Store */}
      <td style={{ padding: '12px 14px', width: 100 }}>
        <span style={{ fontSize: 11, color: '#8b949e' }}>{event.store_id ? 'Store-specific' : 'All stores'}</span>
      </td>

      {/* Actions */}
      <td style={{ padding: '12px 10px', width: 80, textAlign: 'right' }}>
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
          <button onClick={() => onEdit(event)} style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #30363d', background: '#21262d', color: '#e6edf3', cursor: 'pointer', fontSize: 12 }}>Edit</button>
          <button onClick={() => onDelete(event.id)} style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.3)', background: 'transparent', color: '#ef4444', cursor: 'pointer' }}>
            <Trash2 size={13} />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function Automation() {
  const [events, setEvents]       = useState([]);
  const [stores, setStores]       = useState([]);
  const [categories, setCategories] = useState([]);
  const [clocks, setClocks]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [filterStore, setFilterStore] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sr, er, catr, clkr] = await Promise.all([
        axios.get(`${API}/tenants/${TENANT}/stores`, { headers: authHeaders() }),
        axios.get(`${API}/automation/events`, { params: { tenant_id: TENANT }, headers: authHeaders() }),
        axios.get(`${API}/rotations/categories`, { params: { tenant_id: TENANT }, headers: authHeaders() }),
        axios.get(`${API}/rotations/clocks`, { params: { tenant_id: TENANT }, headers: authHeaders() }),
      ]);
      setStores(sr.data?.items || sr.data || []);
      setEvents(er.data || []);
      setCategories(catr.data || []);
      setClocks(clkr.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleSave = async (form) => {
    const payload = { ...form, tenant_id: TENANT, date_from: form.date_from || null, date_to: form.date_to || null };
    if (editEvent) {
      await axios.patch(`${API}/automation/events/${editEvent.id}`, payload, { headers: authHeaders() });
    } else {
      await axios.post(`${API}/automation/events`, payload, { headers: authHeaders() });
    }
    setEditEvent(null);
    loadAll();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    await axios.delete(`${API}/automation/events/${id}`, { headers: authHeaders() });
    loadAll();
  };

  const handleToggle = async (id, active) => {
    await axios.patch(`${API}/automation/events/${id}`, { is_active: active }, { headers: authHeaders() });
    setEvents(prev => prev.map(e => e.id === id ? { ...e, is_active: active } : e));
  };

  const filtered = events.filter(e => !filterStore || !e.store_id || e.store_id === filterStore);
  const activeCount = events.filter(e => e.is_active).length;

  const iStyle = { background: '#161b22', border: '1px solid #30363d', borderRadius: 7, padding: '6px 11px', color: '#e6edf3', fontSize: 13, outline: 'none' };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#e6edf3' }}>Events & Automation</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#8b949e' }}>
            Time-based broadcast triggers — fire automatically at set times with priority ordering
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={filterStore} onChange={e => setFilterStore(e.target.value)} style={iStyle}>
            <option value="">All Stores</option>
            {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button onClick={() => { setEditEvent(null); setShowModal(true); }} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', borderRadius: 7, border: 'none', background: '#2f81f7', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            <Plus size={14} /> New Event
          </button>
        </div>
      </div>

      {/* Summary strip */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total Events', value: events.length, color: '#58a6ff' },
          { label: 'Active', value: activeCount, color: '#22c55e' },
          { label: 'Interrupt-enabled', value: events.filter(e => e.interrupt).length, color: '#ef4444' },
          { label: 'High Priority (≥8)', value: events.filter(e => e.priority >= 8).length, color: '#f59e0b' },
        ].map(s => (
          <div key={s.label} style={{ flex: 1, background: '#161b22', border: '1px solid #30363d', borderRadius: 10, padding: '12px 16px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#8b949e', letterSpacing: '0.07em', marginBottom: 4 }}>{s.label.toUpperCase()}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Events table */}
      <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#484f58' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#484f58', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <AlertCircle size={36} style={{ opacity: 0.4 }} />
            <div style={{ fontSize: 14 }}>No automation events yet — create one to get started</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#1c2333', borderBottom: '1px solid #30363d' }}>
                {['', 'NAME', 'TRIGGER', 'ACTION', 'PRI', 'SCOPE', ''].map((h, i) => (
                  <th key={i} style={{ padding: '9px 14px', textAlign: i >= 4 ? 'center' : 'left', fontSize: 11, fontWeight: 700, color: '#8b949e', letterSpacing: '0.07em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(event => (
                <EventRow
                  key={event.id}
                  event={event}
                  categories={categories}
                  clocks={clocks}
                  onEdit={e => { setEditEvent(e); setShowModal(true); }}
                  onDelete={handleDelete}
                  onToggle={handleToggle}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Priority legend */}
      <div style={{ display: 'flex', gap: 16, marginTop: 16, fontSize: 12, color: '#8b949e' }}>
        <span>Priority: </span>
        {[['1–4', '#22c55e', 'Low'], ['5–7', '#f59e0b', 'Medium'], ['8–10', '#ef4444', 'High/Critical']].map(([range, color, label]) => (
          <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
            {range} {label}
          </span>
        ))}
        <span style={{ marginLeft: 12 }}>⚡ = interrupt current track when fired</span>
      </div>

      {showModal && (
        <EventModal
          event={editEvent}
          stores={stores}
          categories={categories}
          clocks={clocks}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditEvent(null); }}
        />
      )}
    </div>
  );
}
